import React from 'react';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button } from 'rizzui';
import CustomSearchableEntities from '../../../../../components/forms/CustomSearchableEntities';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import useContractStates from '../../../../../hooks/api-calls/contract/useContractStates';
import useClients from '../../../../../hooks/api-calls/useClients';
import useProducts from '../../../../../hooks/api-calls/useProducts';
import useStudents from '../../../../../hooks/api-calls/useStudents';
import useTutors from '../../../../../hooks/api-calls/useTutors';
import { FilterOptions } from '../../../../../hooks/useFilters';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';

interface ContractExportsFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
}

const ContractExportsFilters: React.FC<ContractExportsFiltersProps> = ({ filters, updateFilters, resetFilters }) => {

    const { getContractStatesList } = useContractStates({ active: true, action: 'edit', entity: 'contract' });
    const { fetchProducts } = useProducts();
    const { getTutorsList, updateTutorsHookFilters } = useTutors();
    const { fetchClients } = useClients();
    const { getStudentsList, updateStudentsHookFilters } = useStudents();
    const { userCan } = usePrivilege();

    return (
        <>
        {userCan('get_statuses_for_select', 'statuses') &&
            <CustomSelect
                id="current_status"
                label="Estado de contrato"
                isMulti={true}
                options={getContractStatesList()}
                onChange={(value: { value: string, label: string }[]) => {
                    const statuses = value.map((status) => status.value);
                    if (statuses !== filters.filter_filters?.custom_status?.statuses) {
                        updateFilters({
                            custom_status: {
                                startDate: null,
                                endDate: null,
                                current: 1,
                                entity: "contract",
                                statuses: statuses,
                            }
                        });
                    }
                }}
                value={filters.filter_filters?.custom_status?.statuses ?
                    getContractStatesList().filter((status) => filters.filter_filters?.custom_status?.statuses.includes(status.value)) : []}
            />
            }

            {userCan('get_products_for_select', 'products') &&
                <CustomSearchableEntities
                    id="product"
                    label="Producto"
                    filters={filters}
                    updateFilters={updateFilters}
                    fetchOptions={async (searchText: string | null) => {
                        return fetchProducts({ search_text: searchText }) as Promise<any>;
                    }}
                    permissions={[
                        {
                            group: 'products',
                            action: 'get_products_for_select'
                        }
                    ]}
                />
            }

            {userCan('get_tutors_for_select', 'tutors') && (
                <CustomSearchableEntities
                    id="tutor"
                    label="Tutor"
                    filters={filters}
                    updateFilters={updateFilters}
                    fetchOptions={async (searchText: string | null) => {
                        updateTutorsHookFilters({ finder: searchText });
                        return getTutorsList();
                    }}
                    permissions={[
                        {
                            group: 'contracts',
                            action: 'admin_contracts'
                        },
                        {
                            group: 'tutors',
                            action: 'list_tutors'
                        }
                    ]}
                />
            )}
            
            <CustomSearchableEntities
                id="client"
                label="Cliente"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    return fetchClients({ search_text: searchText, ids: filters.filter_filters?.client ?? [] });
                }}
                permissions={[
                    {
                        group: 'contracts',
                        action: 'admin_contracts'
                    },
                    {
                        group: 'clients',
                        action: 'list_clients'
                    }
                ]}
            />

            {userCan('list_students', 'students') &&
                <CustomSearchableEntities
                    id="student"
                    label="Alumno"
                    filters={filters}
                    updateFilters={updateFilters}
                    fetchOptions={async (searchText: string | null) => {
                        updateStudentsHookFilters({ search_text: searchText });
                        return getStudentsList();
                    }}
                    permissions={[
                        {
                            group: 'contracts',
                            action: 'admin_contracts'
                        },
                        {
                            group: 'students',
                            action: 'list_students'
                        }
                    ]}
                />
            }

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </>
    );
};

export default ContractExportsFilters;